import { Button } from '@repo/design-system/components/ds/button';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { Checkbox } from '@repo/design-system/components/ui/checkbox';
import { Fieldset } from '@repo/design-system/components/ui/fieldset';
import { Form } from '@repo/design-system/components/ui/form';
import { Modal } from '@repo/design-system/components/ui/modal';
import { Select, SelectItem } from '@repo/design-system/components/ui/select';
import { TextArea } from '@repo/design-system/components/ui/text-area';
import { TextInput } from '@repo/design-system/components/ui/text-input';
import { toast } from '@repo/design-system/components/ui/toaster';
import { useT } from '@repo/i18n/components/i18n.client';
import { useDeleteMapSubcategoryMutation, useUpdateMapSubcategoryMutation } from '@repo/store/maps/api/mapsApi';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectExternalMapId, selectMapCategories, selectMapSubcategories, selectTitleSlug } from '@repo/store/maps/slices/mapData';
import { useCallback, useMemo, useState } from 'react';
import { MapIconSelector } from './map-icon-selector';
interface SubcategoryFormState {
  selectedCategoryId: string;
  selectedSubcategoryId: string;
  parentCategoryId: string;
  subcategoryName: string;
  selectedIcon: string;
  description: string;
  excludeFromChecklists: boolean;
  showDeleteModal: boolean;
}
const initialFormState: SubcategoryFormState = {
  selectedCategoryId: '',
  selectedSubcategoryId: '',
  parentCategoryId: '',
  subcategoryName: '',
  selectedIcon: '',
  description: '',
  excludeFromChecklists: false,
  showDeleteModal: false
};
export const EditSubcategoryForm = () => {
  const {
    t
  } = useT(['game_map']);
  const mapSubcategories = useAppSelector(selectMapSubcategories);
  const mapCategories = useAppSelector(selectMapCategories);
  const externalMapId = useAppSelector(selectExternalMapId);
  const titleSlug = useAppSelector(selectTitleSlug);
  const [formState, setFormState] = useState<SubcategoryFormState>(initialFormState);
  const updateFormState = useCallback((updates: Partial<SubcategoryFormState>) => {
    setFormState(prev => ({
      ...prev,
      ...updates
    }));
  }, []);
  const [updateSubcategory, {
    isLoading: isUpdating
  }] = useUpdateMapSubcategoryMutation();
  const [deleteSubcategory, {
    isLoading: isDeleting
  }] = useDeleteMapSubcategoryMutation();
  const filteredSubcategories = useMemo(() => mapSubcategories?.filter(sub => sub.categoryExternalId === formState.selectedCategoryId) || [], [mapSubcategories, formState.selectedCategoryId]);
  const selectedSubcategory = useMemo(() => mapSubcategories?.find(sub_0 => sub_0.externalId === formState.selectedSubcategoryId), [mapSubcategories, formState.selectedSubcategoryId]);
  const categoryOptions = useMemo(() => mapCategories?.map(category => <SelectItem key={category.externalId} item={{
    label: category.title,
    value: category.externalId
  }}>
                    {category.title}
                </SelectItem>), [mapCategories]);
  const subcategoryOptions = useMemo(() => filteredSubcategories.map(subcategory => <SelectItem key={subcategory.externalId} item={{
    label: subcategory.title,
    value: subcategory.externalId
  }}>
                    {subcategory.title}
                </SelectItem>), [filteredSubcategories]);
  const handleSelectSubcategory = useCallback((subcategoryId: string) => {
    const sub_2 = filteredSubcategories.find(sub_1 => sub_1.externalId === subcategoryId);
    if (sub_2) {
      updateFormState({
        selectedSubcategoryId: subcategoryId,
        parentCategoryId: sub_2.categoryExternalId,
        subcategoryName: sub_2.title,
        selectedIcon: sub_2.icon || '',
        description: sub_2.description || '',
        excludeFromChecklists: !!sub_2.excludeFromChecklists
      });
    }
  }, [filteredSubcategories, updateFormState]);
  const handleSave = useCallback(async () => {
    const {
      selectedSubcategoryId,
      subcategoryName,
      selectedIcon,
      parentCategoryId,
      description,
      excludeFromChecklists
    } = formState;
    if (!selectedSubcategoryId || !subcategoryName.trim() || !selectedIcon || !parentCategoryId || !externalMapId || !selectedSubcategory) return;
    try {
      await updateSubcategory({
        subcategory: {
          ...selectedSubcategory,
          title: subcategoryName,
          icon: selectedIcon,
          categoryExternalId: parentCategoryId,
          externalId: selectedSubcategory.externalId || '',
          description,
          excludeFromChecklists
        },
        mapExternalId: externalMapId
      }).unwrap();
      updateFormState({
        selectedCategoryId: parentCategoryId
      });
      toast({
        title: t($_0 => $_0.subcategory_updated),
        description: t($_1 => $_1.subcategory_updated_successfully),
        variant: 'success'
      });
    } catch (error) {
      toast({
        title: t($ => $.failed_to_update_subcategory),
        description: JSON.stringify(error),
        variant: 'warning'
      });
    }
  }, [formState, externalMapId, selectedSubcategory, updateSubcategory, t, updateFormState]);
  const handleDelete = useCallback(async () => {
    const {
      selectedSubcategoryId: selectedSubcategoryId_0
    } = formState;
    if (!selectedSubcategoryId_0 || !externalMapId || !selectedSubcategory) return;
    try {
      await deleteSubcategory({
        subcategory: selectedSubcategory,
        mapExternalId: externalMapId
      }).unwrap();
      updateFormState(initialFormState);
      toast({
        title: t($_3 => $_3.subcategory_deleted),
        description: t($_4 => $_4.subcategory_deleted_successfully),
        variant: 'success'
      });
    } catch (error_0) {
      toast({
        title: t($_2 => $_2.failed_to_delete_subcategory),
        description: JSON.stringify(error_0),
        variant: 'warning'
      });
    }
  }, [formState, externalMapId, selectedSubcategory, deleteSubcategory, updateFormState, t]);
  const isFormValid = formState.selectedSubcategoryId && formState.subcategoryName.trim() && formState.selectedIcon && formState.parentCategoryId;
  return <>
            <Form spacing="md" onSubmit={e => {
      e.preventDefault();
      void handleSave();
    }}>
                <Fieldset legend={t($_5 => $_5.edit_subcategory)} legendHidden spacing="md">
                    <Select label={t($_6 => $_6.source_category)} value={formState.selectedCategoryId} onValueChange={value => {
          updateFormState({
            selectedCategoryId: value,
            selectedSubcategoryId: '',
            parentCategoryId: '',
            subcategoryName: '',
            selectedIcon: '',
            description: '',
            excludeFromChecklists: false
          });
        }} placeholder={t($_7 => $_7.select_category)} size="sm" fullWidth aria-label={t($_8 => $_8.select_category_to_filter_subcategories)} trigger={<Button color="secondary" size="sm" fullWidth>
                                {formState.selectedCategoryId ? mapCategories?.find(cat => cat.externalId === formState.selectedCategoryId)?.title : t($_9 => $_9.select_category)}
                            </Button>}>
                        {categoryOptions}
                    </Select>

                    {formState.selectedCategoryId && <Select label={t($_10 => $_10.subcategory)} value={formState.selectedSubcategoryId} onValueChange={handleSelectSubcategory} placeholder={t($_11 => $_11.select_subcategory)} size="sm" fullWidth aria-label={t($_12 => $_12.select_subcategory_to_edit)} trigger={<Button color="secondary" size="sm" fullWidth>
                                    {selectedSubcategory ? selectedSubcategory.title : t($_13 => $_13.select_subcategory)}
                                </Button>}>
                            {subcategoryOptions}
                        </Select>}

                    {selectedSubcategory && <>
                            <TextInput fullWidth label={t($_14 => $_14.name)} value={formState.subcategoryName} onChange={e_0 => updateFormState({
            subcategoryName: e_0.target.value
          })} placeholder={t($_15 => $_15.subcategory_name)} size="sm" required />
                            <FlexRow gap="12" align="center" width="full" wrap="wrap">
                                <MapIconSelector selectedIcon={formState.selectedIcon} onIconChange={icon => updateFormState({
              selectedIcon: icon
            })} fontId={titleSlug} />
                            </FlexRow>

                            <TextArea fullWidth size="sm" label={t($_16 => $_16.description)} value={formState.description} onChange={e_1 => updateFormState({
            description: e_1.target.value
          })} placeholder="Max 500 characters" maxLength={500} helperText={`${formState.description.length}/500`} />

                            <Checkbox id="exclude-from-checklists-edit" label={t($_17 => $_17.exclude_from_checklists)} checked={formState.excludeFromChecklists} onCheckedChange={details => updateFormState({
            excludeFromChecklists: !!details.checked
          })} size="sm" />

                            <Select label={t($_18 => $_18.parent_category)} value={formState.parentCategoryId} onValueChange={value_0 => updateFormState({
            parentCategoryId: value_0
          })} placeholder={t($_19 => $_19.select_parent_category)} size="sm" fullWidth aria-label={t($_20 => $_20.select_parent_category)} trigger={<Button color="secondary" size="sm" fullWidth>
                                        {formState.parentCategoryId ? mapCategories?.find(cat_0 => cat_0.externalId === formState.parentCategoryId)?.title : t($_21 => $_21.select_parent_category)}
                                    </Button>}>
                                {categoryOptions}
                            </Select>
                        </>}
                </Fieldset>

                <FlexRow gap="4" justify="end">
                    <Button type="button" variant="ghost" onClick={() => updateFormState({
          showDeleteModal: true
        })} color="error" size="sm" disabled={!formState.selectedSubcategoryId || isDeleting}>
                        {isDeleting ? t($_22 => $_22.deleting) : t($_23 => $_23.delete)}
                    </Button>
                    <Button type="submit" color="success" size="sm" disabled={!isFormValid || isUpdating}>
                        {isUpdating ? t($_24 => $_24.saving) : t($_25 => $_25.save_changes)}
                    </Button>
                </FlexRow>
            </Form>

            <Modal isOpen={formState.showDeleteModal} onOpenChange={() => updateFormState({
      showDeleteModal: false
    })} title={t($_26 => $_26.delete_subcategory)} description={t($_27 => $_27.delete_subcategory_confirmation, {
      title: selectedSubcategory?.title
    })} size="sm">
                <FlexRow gap="4" justify="end">
                    <Button variant="ghost" onClick={() => updateFormState({
          showDeleteModal: false
        })} color="secondary" size="sm">
                        {t($_28 => $_28.cancel)}
                    </Button>
                    <Button onClick={handleDelete} color="error" size="sm" disabled={isDeleting}>
                        {isDeleting ? t($_29 => $_29.deleting) : t($_30 => $_30.delete)}
                    </Button>
                </FlexRow>
            </Modal>
        </>;
};